import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import styles from './TeamsPage.module.css'
import o from '../components/Overlay.module.css'
import { useRefetch } from '../lib/connection'
import { useAuth } from '../contexts/AuthContext'
import { can, useAccess } from '../lib/access'
import { apiJson, ApiError } from '../lib/api'
import { useHelpPref } from '../lib/preferences'
import { useChanged, useLayer } from '../components/layers'
import { SkeletonRows, PendingButton, useSlow, inertClass } from '../components/pending'
import { PageHeader, PageBody, TabBar, headerStyles } from '../components/PageHeader'
import { wallOf, instantOf } from '../lib/timezones'
import { businessZoneName } from './timesheets/time'
import { TeamQrDialog } from './teams/TeamQrDialog'
import { InductionEmailDialog } from './teams/InductionEmailDialog'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { QR_INDUCTION_NOTE, QR_SCAN_NOTE, type QrCard } from './teams/qrCard'
import { openableRow } from '../lib/openableRow'
import ps from './PersonnelPage.module.css'
import { TeamBulkPanel } from './teams/TeamBulkPanel'
import { TeamsSettingsModal } from './teams/TeamsSettingsModal'

// Teams (CONTROL V4 "Teams", handoff §7e and "Every team code has a lifetime"
// in §7c). A team is what someone is doing today: a task, a site and the
// phone settings that go with them. The list opens a slide-out panel; the
// panel is a draft until Save, except New code / Reset key, which the hint
// promises take effect straight away.
//
// The clock-on QR is a Dataphyll Crew QR v2 (docs/crew-qr-v2.md): the API
// builds the square for a fixed code and the dialog prints it; a rotating code
// lives on a supervisor's phone. Not built: the induction email (no mail path).

export interface Team {
  id: string
  name: string
  task: string
  mode: string
  orchard_id: string | null
  site_name: string | null
  site_archived: boolean | null
  product_id: string | null
  colour: string | null
  active: boolean
  is_induction: boolean
  pay_basis: 'hourly' | 'piece_rate'
  preassign: boolean
  quality_set_id: string | null
  quality_set_name: string | null
  access_code: string | null
  code_life: LifeKey
  code_rotating: boolean
  code_issued_at: string | null
  code_expires_at: string | null
  stand_down_secs: number | null
  location_re_ask_mins: number | null
  bin_idle_mins: number | null
  location_required: boolean
  location_entry: LocationEntry | null
  clock_off_at: string
  group_id: string | null
  group_name: string | null
  default_team_id: string | null
  default_team_name: string | null
}
interface Site {
  id: string
  name: string
  archived_at: string | null
}
interface QcSet {
  id: string
  name: string
}
interface Group {
  id: string
  name: string
  is_archived: boolean
}

export type LifeKey = '20m' | '1h' | '4h' | '12h' | '1d' | '1w' | '1mo' | '3mo' | '6mo' | '1y'
// The picker offers one rotating choice, 'Realtime via Supervisor' (a 20-minute
// code on the supervisor's phone), then the printed lifetimes. 1, 4 and 12 hours
// still work and are shown only to a team already set to one (legacy).
export const LIFETIMES: { key: LifeKey; label: string; option?: string; legacy?: boolean; min?: number; mon?: number }[] = [
  { key: '20m', label: '20 minutes', option: 'Realtime via Supervisor', min: 20 },
  { key: '1h', label: '1 hour', legacy: true, min: 60 },
  { key: '4h', label: '4 hours', legacy: true, min: 240 },
  { key: '12h', label: '12 hours', legacy: true, min: 720 },
  { key: '1d', label: '1 day', min: 1440 },
  { key: '1w', label: '1 week', min: 10080 },
  { key: '1mo', label: '1 month', mon: 1 },
  { key: '3mo', label: '3 months', mon: 3 },
  { key: '6mo', label: '6 months', mon: 6 },
  { key: '1y', label: '1 year', mon: 12 },
]
const lifeOf = (k: string) => LIFETIMES.find((l) => l.key === k) || LIFETIMES[LIFETIMES.length - 1]
export const isRotating = (k: string) => {
  const l = lifeOf(k)
  return !!l.min && l.min < 1440
}

export const TASKS = ['Harvest', 'Field work', 'Packhouse', 'Supervisor', 'Induction']
export const PRODUCTS = ['Apples', 'Cherries', 'Blueberries', 'Kiwifruit', 'Grapes']
export const MODES: { key: string; desc: string }[] = [
  { key: 'Harvest', desc: 'Pickers scan on, scan each bucket and take photos.' },
  { key: 'Harvest readonly', desc: 'Like Selfscan Harvest, but with no scan button: pickers see their numbers and don’t scan items.' },
  { key: 'Tagless harvest', desc: 'Pickers show their phone’s QR to the receipter instead of scanning buckets.' },
  { key: 'Receipting', desc: 'Multi-scan items straight in.' },
  { key: 'Receipt into a bin', desc: 'Scan a bin first, then scan items into it.' },
  { key: 'Supervisor', desc: 'Shows the live clock-on QR and code for this site’s rotating teams. Workers scan this phone to clock on.' },
]
// What a mode is called on screen. The key is what a team stores and its
// clock-on QR carries, so a new name here needs no reprint.
const MODE_LABELS: Record<string, string> = { Harvest: 'Selfscan Harvest', 'Harvest readonly': 'Harvest Readonly' }
export const modeLabel = (k: string) => MODE_LABELS[k] || k
const RECEIPTING = ['Receipting', 'Receipt into a bin']
// The modes whose phone can QC an item: the receivers, and a supervisor walking the rows.
const QC_MODES = [...RECEIPTING, 'Supervisor']
// What Client Setup turns on for this client (GET /api/teams/offer). What is
// off stays listed but cannot be chosen, unless the team already has it.
export interface TeamOffer {
  set_up: boolean
  modes: { k: string; on: boolean }[]
  pay_bases: { k: string; on: boolean }[]
}
export const offered = (list: { k: string; on: boolean }[] | undefined, k: string) => !list || list.find((x) => x.k === k)?.on !== false
const NOT_ON = 'Not turned on for this client. Dataphyll turns it on in Client Setup.'
// The app's own defaults when a team leaves a timing blank (V4 FLOW_DEFAULTS).
const FLOW_DEFAULTS = { stand: 0, reask: 0, binIdle: 60 }
const CLOCK_OFF_DEFAULT = '23:00'

// An admin picks a colour, never a hex (V4 TEAM_COLORS).
export const TEAM_COLOURS = [
  { name: 'Apricot', hex: '#d9b38a' },
  { name: 'Sage', hex: '#c9d3ae' },
  { name: 'Sky', hex: '#b9c6d6' },
  { name: 'Rose', hex: '#e0c3c0' },
  { name: 'Straw', hex: '#e3d2a8' },
  { name: 'Mint', hex: '#bcd4c8' },
  { name: 'Lilac', hex: '#d4c7dd' },
  { name: 'Stone', hex: '#cfc7b0' },
]
const colourName = (hex: string) => TEAM_COLOURS.find((c) => c.hex === hex.toLowerCase())?.name || 'Pick one'

const HELP: [string, string][] = [
  [
    'A team is what someone is doing today',
    'Not who employs them. That is a group, and a person only has one. A person can work across several teams in a day and be on different ones tomorrow, so teams are the operational side and groups are the payroll side.',
  ],
  [
    'Clocking on to a team makes the timesheet',
    "Each clock-on and clock-off pair is a session against that team. Move to another team mid-morning and you get a second session, so one person's day can be several rows. Wages read those sessions, which is why a team and its hours have to be right before a week is closed.",
  ],
  [
    'The task decides what the app can do',
    'Task and app mode unlock functions in Dataphyll Crew. A Packhouse team gets the receipting screens, a harvest team gets picking with tags or tagless, and quality scanning appears only where the team has a quality set. Someone on the wrong team simply will not see the buttons they need.',
  ],
  [
    'Induction teams do not make timesheets',
    'An induction team exists so people can register themselves before their first day. A scan on it creates the person and nothing else. No hours start and nothing reaches wages, so never hand the induction code or QR to someone who is actually working. Give them the code for the team they are working on.',
  ],
  [
    'Access codes and the preassign team',
    "Every team has an access code, a typed alternative to scanning its QR, and the two share a lifetime set on the team: 20 minutes to 1 year. Under a day the code rotates and only shows live on a phone in Supervisor app mode, so a photo of it stops working. A day or more is a fixed code you can print, replaced automatically when it expires. Induction invitations carry each person's own code, valid for a set time from sending. One team per site can be the preassign team, where a worker's first tag scan counts as their clock-on.",
  ],
]
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
// The day on the tenant's calendar, the clock renewals run on (E7).
export const fmtDay = (d: Date) => {
  const w = wallOf(d, businessZoneName())
  return `${w.d} ${MONTHS[w.mo]} ${w.y}`
}

// issued + n lifetimes, the way the server's SQL computes it
// (routes/teams.js lifetimesAfter): absolute minutes for the rotating
// lifetimes; for a day or more, calendar days and months on the tenant's
// clock, keeping the wall-clock time across daylight saving, with the day
// clamped to the month's end as Postgres does (31 Jan + 1 month = 28 Feb),
// never stepped one lifetime at a time from a clamped date.
function addLifetimes(issued: Date, key: LifeKey, n: number) {
  const l = lifeOf(key)
  const zone = businessZoneName()
  const w = wallOf(issued, zone)
  if (l.mon) {
    const months = w.mo + n * l.mon
    const y = w.y + Math.floor(months / 12)
    const mo = ((months % 12) + 12) % 12
    const last = new Date(Date.UTC(y, mo + 1, 0)).getUTCDate()
    return instantOf({ ...w, y, mo, d: Math.min(w.d, last) }, zone)
  }
  const min = l.min || 0
  if (min < 1440) return new Date(issued.getTime() + n * min * 60000)
  const day = new Date(Date.UTC(w.y, w.mo, w.d + (n * min) / 1440))
  return instantOf({ ...w, y: day.getUTCFullYear(), mo: day.getUTCMonth(), d: day.getUTCDate() }, zone)
}

// When a fixed code would next be replaced under a lifetime: the first
// boundary after now. `passed` means the current code's replacement date has
// already gone under this lifetime, so saving replaces the code.
export function nextReplacement(issuedAt: string, key: LifeKey, now = new Date()) {
  const issued = new Date(issuedAt)
  let n = 1
  while (addLifetimes(issued, key, n) <= now && n < 100000) n++
  return { date: addLifetimes(issued, key, n), passed: n > 1 }
}

export const isInduction = (t: { task: string }) => t.task === 'Induction'

export function codeLine(t: Team) {
  const l = lifeOf(t.code_life)
  if (t.code_rotating) return `Rotating code · every ${l.label} on the supervisor’s phone`
  if (!t.access_code) return null
  if (isInduction(t)) return `Invitation codes · valid ${l.label} from sending`
  return `Access code ${t.access_code}${t.code_expires_at ? ` · renews ${fmtDay(new Date(t.code_expires_at))}` : ''}`
}

// The clock-on QR is offered only while it could clock anyone on: an inactive
// team's is dead (V4 h 18118), and the list says why rather than hiding it.
export const qrHint = (t: Team) =>
  t.active ? 'Show and print this team’s clock-on QR' : 'No clock-on QR. This team is inactive. Any printed QR still scans offline, so collect them in'

// When the clock-on QR lives on a supervisor's phone rather than on paper,
// the sentence the dialog shows in place of the square; null when there is
// a card to print. Only a rotating code is never printed (a photo of it would
// stop working). A Tagless harvest team clocks on from its card like any
// other; Tagless only changes what the phone does once clocked on.
export function phoneNote(t: Team): string | null {
  const at = t.site_name ? ` at ${t.site_name}` : ''
  if (t.code_rotating)
    return `This team’s code rotates every ${lifeOf(t.code_life).label}, so there is no QR to print. A phone on a team in Supervisor app mode${at} shows the live QR. Workers scan the supervisor’s phone to clock on.`
  return null
}

export function teamQrCard(t: Team): QrCard {
  const l = lifeOf(t.code_life)
  return {
    kind: isInduction(t) ? 'Induction QR: no timesheet' : 'Clock-on QR',
    teamName: t.name,
    accent: RECEIPTING.includes(t.mode) && !isInduction(t) ? 'receipt' : 'harvest',
    note: isInduction(t) ? QR_INDUCTION_NOTE : QR_SCAN_NOTE,
    modules: [],
    accessCode: t.code_rotating || isInduction(t) ? null : t.access_code,
    rows: [
      { label: 'Team', value: t.name },
      { label: 'Site', value: t.site_name || '—' },
      { label: 'Task', value: t.task },
      { label: 'Mode', value: isInduction(t) ? 'Induction' : modeLabel(t.mode) },
      { label: 'Status', value: t.active ? 'Active' : 'Inactive' },
      t.code_rotating
        ? { label: 'Code', value: `Rotates every ${l.label}` }
        : isInduction(t)
          ? { label: 'Invitation codes', value: `Valid ${l.label} from sending` }
          : { label: 'Replaced on', value: t.code_expires_at ? fmtDay(new Date(t.code_expires_at)) : '—' },
    ],
  }
}

type Tab = 'active' | 'inactive' | 'induction'
const inTab = (t: Team, tab: Tab) => (tab === 'induction' ? isInduction(t) : !isInduction(t) && (tab === 'inactive' ? !t.active : t.active))

interface Draft {
  id: string | null
  name: string
  task: string
  orchardId: string
  productId: string
  colour: string
  active: boolean
  preassign: boolean
  payBasis: 'hourly' | 'piece_rate'
  codeLife: LifeKey
  mode: string
  qualitySetId: string | null
  stand: string
  reask: string
  binIdle: string
  locationRequired: boolean
  locationEntry: LocationEntry | ''
  clockOff: string
  groupId: string
  defaultTeamId: string
}
type LocationEntry = 'scan' | 'manual' | 'both' | 'none'
// The phone's `locentry`; blank is the app's own default, either way.
export const LOCATION_ENTRY_OPTIONS: { value: LocationEntry | ''; label: string }[] = [
  { value: '', label: 'Scan or pick (default)' },
  { value: 'scan', label: 'Location QR only' },
  { value: 'manual', label: 'Pick from the list only' },
  { value: 'none', label: 'Not asked' },
]
const num = (v: number | null) => (v === null || v === undefined ? '' : String(v))
const toDraft = (t: Team | null): Draft =>
  t
    ? {
        id: t.id,
        name: t.name,
        task: t.task,
        orchardId: t.orchard_id || '',
        productId: t.product_id || '',
        colour: t.colour || '',
        active: t.active,
        preassign: t.preassign,
        payBasis: t.pay_basis,
        codeLife: t.code_life,
        mode: t.mode,
        qualitySetId: t.quality_set_id,
        stand: num(t.stand_down_secs),
        reask: num(t.location_re_ask_mins),
        binIdle: num(t.bin_idle_mins),
        locationRequired: !!t.location_required,
        locationEntry: t.location_entry || '',
        clockOff: t.clock_off_at || CLOCK_OFF_DEFAULT,
        groupId: t.group_id || '',
        defaultTeamId: t.default_team_id || '',
      }
    : {
        id: null,
        name: '',
        task: '',
        orchardId: '',
        productId: '',
        colour: '',
        active: true,
        preassign: false,
        payBasis: 'hourly',
        codeLife: '1y',
        mode: 'Harvest',
        qualitySetId: null,
        stand: '',
        reask: '',
        binIdle: '',
        locationRequired: false,
        locationEntry: '',
        clockOff: CLOCK_OFF_DEFAULT,
        groupId: '',
        defaultTeamId: '',
      }
const digits = (v: string) => v.replace(/[^0-9]/g, '').slice(0, 4)
const flowValue = (v: string) => (v === '' ? null : parseInt(v, 10))

export function TeamsPage() {
  const { user } = useAuth()
  // Teams: Add for ADMIN and OPERATIONS, Read for PAYROLL (D1.1). A reader
  // sees the teams and their settings, and is offered no change.
  const access = useAccess()
  const canAdd = can(access, 'teams', 'add')
  const canEdit = can(access, 'teams', 'edit')
  const [params, setParams] = useSearchParams()
  const [teams, setTeams] = useState<Team[] | null>(null)
  const [sites, setSites] = useState<Site[]>([])
  const [sets, setSets] = useState<QcSet[]>([])
  const [groups, setGroups] = useState<Group[]>([])
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('active')
  // The team whose clock-on QR dialog is open.
  const [qrId, setQrId] = useState<string | null>(null)
  // The induction team whose email template is open.
  const [mailId, setMailId] = useState<string | null>(null)
  const { currentWorkspace } = useWorkspace()
  const [q, setQ] = useState('')
  const [help, setHelp] = useHelpPref(user, 'teams')
  const [draft, setDraft] = useState<Draft | null>(null)
  const [preWarn, setPreWarn] = useState<string | null>(null)
  // D5.9: switching off the team that holds the preassign role clears the
  // role, and says so first. True once the person has been told and has not
  // yet confirmed.
  const [clearAsk, setClearAsk] = useState(false)
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState('')
  const [cog, setCog] = useState(false)
  const [offer, setOffer] = useState<TeamOffer | null>(null)
  // Ticked teams (the Personnel pattern): one opens its own panel, more open
  // the bulk edit.
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [bulkOpen, setBulkOpen] = useState(false)

  const userRef = useRef(user)
  userRef.current = user
  const uid = user?.uid
  const load = useCallback(async () => {
    const u = userRef.current
    if (!u) return
    setError(null)
    try {
      const [t, s, qc, of, gs] = await Promise.all([
        apiJson<Team[]>(u, '/api/teams'),
        // Sites & jobs is its own area (PAYROLL has none): without it the
        // orchard list is empty, and each team still names its orchard.
        apiJson<Site[]>(u, '/api/sites').catch((e) => {
          if (e instanceof ApiError && e.status === 403) return [] as Site[]
          throw e
        }),
        apiJson<{ sets: QcSet[] }>(u, '/api/quality').catch(() => ({ sets: [] as QcSet[] })),
        apiJson<TeamOffer>(u, '/api/teams/offer').catch(() => null),
        // Without Groups the list is empty, and each team still names its group.
        apiJson<Group[]>(u, '/api/groups').catch(() => [] as Group[]),
      ])
      setOffer(of && of.set_up ? of : null)
      setTeams(t)
      setSites(s)
      setSets(qc.sets.map((x) => ({ id: x.id, name: x.name })))
      setGroups(gs.map((g) => ({ id: g.id, name: g.name, is_archived: g.is_archived })))
    } catch (e: any) {
      setError(e?.message || 'Could not load the teams')
    }
  }, [uid])

  useEffect(() => {
    load()
  }, [load])
  useRefetch(load)

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 3200)
    return () => clearTimeout(t)
  }, [toast])

  // Quality control links here with ?team=<id> (Open team ›).
  const linked = params.get('team')
  useEffect(() => {
    if (!teams || !linked) return
    const t = teams.find((x) => x.id === linked)
    if (t) {
      setTab(isInduction(t) ? 'induction' : t.active ? 'active' : 'inactive')
      setDraft(toDraft(t))
      setPreWarn(null)
    }
  }, [teams, linked])

  // Global search's "See all in Teams" fills the search box (h 465-485).
  const linkedQ = params.get('q')
  useEffect(() => {
    if (linkedQ === null) return
    setQ(linkedQ)
    const next = new URLSearchParams(params)
    next.delete('q')
    setParams(next, { replace: true })
  }, [linkedQ])

  const open = (t: Team | null) => {
    const d = toDraft(t)
    if (!t && offer) {
      if (!offered(offer.modes, d.mode)) d.mode = offer.modes.find((m) => m.on)?.k || d.mode
      if (!offered(offer.pay_bases, d.payBasis)) d.payBasis = (offer.pay_bases.find((p) => p.on)?.k as Draft['payBasis']) || d.payBasis
    }
    setDraft(d)
    setPreWarn(null)
    setClearAsk(false)
    if (t) setParams({ team: t.id }, { replace: true })
  }
  const close = () => {
    setDraft(null)
    setPreWarn(null)
    setClearAsk(false)
    if (params.get('team')) setParams({}, { replace: true })
  }
  const patch = (p: Partial<Draft>) => setDraft((d) => d && { ...d, ...p })

  const all = teams || []
  // The first load draws skeleton rows (h 3994-4002); the tabs and the
  // search filter what is already here, so they never wait.
  const loading = !teams && !error
  const saved = draft?.id ? all.find((t) => t.id === draft.id) || null : null
  const qrTeam = qrId ? all.find((t) => t.id === qrId && t.active) || null : null
  const mailTeam = mailId ? all.find((t) => t.id === mailId && t.access_code) || null : null
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return all
      .filter((t) => inTab(t, tab))
      .filter((t) => !needle || [t.name, t.site_name, t.group_name, t.task, t.mode, modeLabel(t.mode), t.product_id, t.access_code].some((v) => v && v.toLowerCase().includes(needle)))
  }, [all, tab, q])
  const rowIds = rows.map((t) => t.id)
  const shownSelected = rowIds.filter((id) => selected.has(id)).length
  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const toggleAll = () => setSelected(shownSelected === rowIds.length ? new Set() : new Set(rowIds))
  const picked = all.filter((t) => selected.has(t.id))
  const changeTab = (k: Tab) => {
    setTab(k)
    setSelected(new Set())
  }
  const editSelection = () => {
    if (picked.length === 1) open(picked[0])
    else if (picked.length > 1) setBulkOpen(true)
  }
  const onBulkSaved = (message: string) => {
    setBulkOpen(false)
    setSelected(new Set())
    setToast(message)
    load()
  }
  const counts = {
    active: all.filter((t) => inTab(t, 'active')).length,
    inactive: all.filter((t) => inTab(t, 'inactive')).length,
    induction: all.filter((t) => inTab(t, 'induction')).length,
  }

  const save = async (confirmClear = false) => {
    if (!draft || busy) return
    const d = draft
    // The saved team holds the role and the draft switches it off: the role
    // goes with it, so ask first. (The server asks too, whatever this list says.)
    if (d.id && saved?.preassign && d.preassign && !d.active && !confirmClear) {
      setClearAsk(true)
      return
    }
    const body: Record<string, unknown> = {
      name: d.name.trim(),
      task: d.task,
      orchardId: d.orchardId,
      productId: d.productId || null,
      colour: d.colour || TEAM_COLOURS[TEAM_COLOURS.length - 1].hex,
      active: d.active,
      mode: d.mode,
      payBasis: d.payBasis,
      codeLife: d.codeLife,
      standDownSecs: flowValue(d.stand),
      locationReAskMins: flowValue(d.reask),
      binIdleMins: flowValue(d.binIdle),
      locationRequired: d.locationRequired,
      clockOffAt: d.clockOff,
    }
    if (d.id && d.preassign !== saved?.preassign) body.preassign = d.preassign
    if (confirmClear) body.confirmClearPreassign = true
    if (d.qualitySetId !== (saved?.quality_set_id ?? null)) body.qualitySetId = d.qualitySetId
    if ((d.locationEntry || null) !== (saved?.location_entry ?? null)) body.locationEntry = d.locationEntry || null
    if ((d.groupId || null) !== (saved?.group_id ?? null)) body.groupId = d.groupId || null
    const defaultTeamId = d.task === 'Induction' ? d.defaultTeamId || null : null
    if (defaultTeamId !== (saved?.default_team_id ?? null)) body.defaultTeamId = defaultTeamId
    setBusy(true)
    try {
      const team = await apiJson<Team>(user, d.id ? `/api/teams/${d.id}` : '/api/teams', { method: d.id ? 'PUT' : 'POST', body })
      setTeams((ts) => {
        const list = ts || []
        return list.some((x) => x.id === team.id) ? list.map((x) => (x.id === team.id ? team : x)) : [...list, team]
      })
      setToast(d.id ? `${team.name} saved` : `${team.name} created`)
      close()
      // A save can move the preassign role or renew codes on other rows.
      load()
    } catch (e: any) {
      if (e instanceof ApiError && e.body?.code === 'PREASSIGN_TAKEN') {
        setPreWarn(e.body.holder)
        patch({ preassign: false })
      } else if (e instanceof ApiError && e.body?.code === 'PREASSIGN_CLEAR_CONFIRM') {
        setClearAsk(true)
      } else if (e instanceof ApiError && e.body?.code === 'PREASSIGN_NEEDS_ACTIVE') {
        patch({ preassign: false })
        setToast(e.body.error)
      } else {
        setToast(e?.message || 'The team was not saved')
      }
    } finally {
      setBusy(false)
    }
  }

  const replaceCode = async () => {
    if (!saved || busy) return
    setBusy(true)
    try {
      const r = await apiJson<{ team: Team; message: string }>(user, `/api/teams/${saved.id}/code`, { method: 'POST' })
      setTeams((ts) => (ts || []).map((x) => (x.id === r.team.id ? r.team : x)))
      setToast(r.message)
    } catch (e: any) {
      setToast(e?.message || 'The code was not replaced')
    } finally {
      setBusy(false)
    }
  }

  if (!teams && !error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <PageHeader title="Teams" />
        <PageBody>
          <p>Loading...</p>
        </PageBody>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <PageHeader
        title="Teams"
        actions={
          <>
            {canAdd && (
              <button type="button" className={headerStyles.btnPrimary} onClick={() => open(null)} disabled={!teams}>
                + New team
              </button>
            )}
            <button type="button" className={headerStyles.btnHelp} onClick={() => setHelp(!help)}>
              {help ? 'Hide help' : 'How teams work'}
            </button>
            {canEdit && (
              <button
                type="button"
                className={headerStyles.iconBtn}
                aria-label="Team settings"
                title="Team settings: the Bin Counter on Crew’s Receipting screen"
                onClick={() => setCog(true)}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6 1.65 1.65 0 0 0 10 3.09V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
                </svg>
              </button>
            )}
          </>
        }
      />

      <PageBody>
        {help && (
          <div className={styles.helpGridOuter}>
            <button type="button" className={styles.helpClose} aria-label="Close help" onClick={() => setHelp(false)}>
              ×
            </button>
            <div className={styles.helpGrid}>
              {HELP.map(([h, body]) => (
                <div key={h} className={styles.helpCard}>
                  <span>{h}</span>
                  <p>{body}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className={styles.errorBar} role="alert">
            <span>{error}</span>
            {!teams && (
              <button type="button" className={styles.ghostSmall} onClick={load}>
                Retry
              </button>
            )}
            <button type="button" className={styles.flashX} aria-label="Dismiss" onClick={() => setError(null)}>
              ×
            </button>
          </div>
        )}

        <div className={styles.searchRow}>
          <input className={styles.search} placeholder="Search teams…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search teams" />
        </div>

        {canEdit && picked.length > 0 && (
          <div className={ps.selBar} aria-label="Selection">
            <span className={ps.selLabel}>
              {picked.length} {picked.length === 1 ? 'team selected' : 'teams selected'}
            </span>
            {shownSelected < rowIds.length && (
              <button type="button" className={ps.selLink} onClick={() => setSelected(new Set(rowIds))}>
                Select all {rowIds.length}
              </button>
            )}
            <button type="button" className={ps.selClear} onClick={() => setSelected(new Set())}>
              Clear
            </button>
            <div className={ps.selActions}>
              <button type="button" className={ps.selMerge} onClick={editSelection}>
                {picked.length === 1 ? 'Edit team' : `Bulk edit ${picked.length}`}
              </button>
            </div>
          </div>
        )}

        <div className={styles.card}>
          <div className={styles.cardTabs}>
          <div className={styles.cardTabsTrack} role="tablist" aria-label="Team status">
            {(['active', 'inactive', 'induction'] as Tab[]).map((k) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? styles.cardTabOn : styles.cardTab} onClick={() => changeTab(k)}>
                <span>{k === 'active' ? 'Active' : k === 'inactive' ? 'Inactive' : 'Induction'}</span>
                <span className={tab === k ? styles.cardTabCount : styles.cardTabCountOff}>{counts[k]}</span>
              </button>
            ))}
          </div>
          </div>
        <div className={canEdit ? `${styles.table} ${styles.selectable}` : styles.table} role="table" aria-label="Teams">
          <div className={styles.headRow} role="row">
            {canEdit && (
              <span role="columnheader" className={styles.checkCol}>
                <PageCheckbox checked={rowIds.length > 0 && shownSelected === rowIds.length} partial={shownSelected > 0 && shownSelected < rowIds.length} disabled={rowIds.length === 0 || loading} onChange={toggleAll} />
              </span>
            )}
            {['Team', 'Site', 'Group', 'Task', tab === 'induction' ? 'Email' : 'Primary product', 'Clock-on QR'].map((h) => (
              <span key={h} role="columnheader" className={h === 'Clock-on QR' ? styles.qrHeadCell : undefined}>
                {h}
              </span>
            ))}
          </div>
          {loading ? (
            <SkeletonRows rowClass={styles.row} cols={canEdit ? 7 : 6} label="Loading teams" />
          ) : rows.length === 0 ? (
            <div className={styles.empty}>
              {!teams ? 'The teams could not be loaded.' : q.trim() ? 'No team matches that search.' : tab === 'induction' ? 'No induction teams.' : tab === 'inactive' ? 'No inactive teams.' : 'No teams yet. Create one to get started.'}
            </div>
          ) : (
            rows.map((t) => {
              const line = codeLine(t)
              const receipting = RECEIPTING.includes(t.mode)
              const modeText = isInduction(t) ? 'Induction' : modeLabel(t.mode)
              return (
                <div key={t.id} role="row" data-testid="team-row" className={t.active ? styles.row : styles.rowOff} {...openableRow(() => open(t))}>
                  {canEdit && (
                    <div className={styles.checkCol} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                      <input type="checkbox" aria-label={`Select ${t.name}`} checked={selected.has(t.id)} onChange={() => toggle(t.id)} />
                    </div>
                  )}
                  <div className={styles.teamCell}>
                    <span className={styles.swatch} style={{ background: t.colour || 'var(--dp-inset)' }} />
                    <div className={styles.teamText}>
                      <div className={styles.nameLine}>
                        <span className={styles.name}>{t.name}</span>
                        {!t.active && <span className={styles.chipOff}>Inactive</span>}
                        {t.preassign && (
                          <span className={styles.chipPre} title="First tag scan at the preassign station clocks a worker onto this team">
                            Preassign
                          </span>
                        )}
                      </div>
                      {line && <div className={styles.codeLine}>{line}</div>}
                    </div>
                  </div>
                  <span className={styles.cell}>{t.site_name || '—'}</span>
                  <span className={t.group_name ? styles.cell : styles.cellNone}>{t.group_name || 'Default'}</span>
                  <div className={styles.taskCell}>
                    <span className={styles.taskName}>{t.task}</span>
                    {/* The mode's chip, unless the task already says it. */}
                    {modeText.toLowerCase() !== t.task.toLowerCase() && (
                      <span className={isInduction(t) ? styles.modeNone : receipting ? styles.modeSage : t.mode === 'Supervisor' ? styles.modeBlue : styles.modeAccent}>{modeText}</span>
                    )}
                  </div>
                  {isInduction(t) ? (
                    <div className={styles.cell}>
                      <button
                        type="button"
                        className={styles.mailLink}
                        disabled={!t.access_code}
                        title={t.access_code ? 'The email new starters get, with this team’s code, to copy into Outlook' : 'The team has no code yet'}
                        onClick={(e) => {
                          e.stopPropagation()
                          setMailId(t.id)
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <rect x="2" y="4" width="20" height="16" rx="2" />
                          <path d="m22 7-10 6L2 7" />
                        </svg>
                        Template
                      </button>
                    </div>
                  ) : (
                    <span className={t.product_id ? styles.cell : styles.cellNone}>{t.product_id || 'None'}</span>
                  )}
                  <div className={styles.qrCell}>
                    <button
                      type="button"
                      className={t.active ? styles.qrBtn : styles.qrBtnOff}
                      aria-label={`Clock-on QR for ${t.name}`}
                      aria-disabled={!t.active}
                      title={qrHint(t)}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (t.active) setQrId(t.id)
                      }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="3" y="3" width="7" height="7" rx="1.5" />
                        <rect x="14" y="3" width="7" height="7" rx="1.5" />
                        <rect x="3" y="14" width="7" height="7" rx="1.5" />
                        <path d="M14 14h3v3M20 20h.01M17 20h.01M20 14h.01" />
                      </svg>
                    </button>
                  </div>
                </div>
              )
            })
          )}
        </div>
          <div className={styles.foot}>
            <span className={styles.footLabel}>
              {rows.length} {tab === 'active' ? 'active' : tab === 'inactive' ? 'inactive' : 'induction'} {rows.length === 1 ? 'team' : 'teams'}
            </span>
            <span className={styles.footNote}>Changing a team's settings means reprinting its clock-on QR</span>
          </div>
        </div>
      </PageBody>

      {draft && (
        <TeamPanel
          draft={draft}
          saved={saved}
          teams={all}
          sites={sites}
          sets={sets}
          groups={groups}
          busy={busy}
          preWarn={preWarn}
          setPreWarn={setPreWarn}
          patch={patch}
          onClose={close}
          onSave={save}
          clearAsk={clearAsk}
          onReplaceCode={replaceCode}
          canEdit={canEdit}
          offer={offer}
        />
      )}

      {bulkOpen && picked.length > 1 && (
        <TeamBulkPanel teams={picked} sites={sites} sets={sets} groups={groups} offer={offer} onClose={() => setBulkOpen(false)} onSaved={onBulkSaved} />
      )}

      {qrTeam && (
        <TeamQrDialog
          teamId={qrTeam.id}
          siteId={qrTeam.orchard_id}
          card={teamQrCard(qrTeam)}
          phoneNote={phoneNote(qrTeam)}
          printedOn={fmtDay(new Date())}
          onClose={() => setQrId(null)}
        />
      )}

      {mailTeam && (
        <InductionEmailDialog
          teamName={mailTeam.name}
          clientName={currentWorkspace?.name || ''}
          code={mailTeam.access_code!}
          expiresOn={mailTeam.code_expires_at ? fmtDay(new Date(mailTeam.code_expires_at)) : null}
          onClose={() => setMailId(null)}
        />
      )}

      {cog && (
        <TeamsSettingsModal
          user={user}
          onClose={() => setCog(false)}
          onSaved={(msg) => {
            setCog(false)
            setToast(msg)
          }}
        />
      )}
      {toast && (
        <div className={styles.toast} role="status">
          {toast}
        </div>
      )}
    </div>
  )
}

function Switch({ on, label, disabled, onClick }: { on: boolean; label: string; disabled?: boolean; onClick: () => void }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} className={o.switch} onClick={onClick} />
  )
}

interface PanelProps {
  draft: Draft
  saved: Team | null
  teams: Team[]
  sites: Site[]
  sets: QcSet[]
  groups: Group[]
  busy: boolean
  preWarn: string | null
  setPreWarn: (h: string | null) => void
  patch: (p: Partial<Draft>) => void
  onClose: () => void
  onSave: (confirmClear?: boolean) => void | Promise<void>
  // D5.9: asking whether to clear the preassign role along with the switch-off.
  clearAsk: boolean
  onReplaceCode: () => void
  // False for a Teams reader: the panel shows the team, with no Save and no
  // code replacement (the server refuses both anyway).
  canEdit: boolean
  offer: TeamOffer | null
}

function TeamPanel({ draft: d, saved, teams, sites, sets, groups, busy, preWarn, setPreWarn, patch, onClose, onSave, clearAsk, onReplaceCode, canEdit, offer }: PanelProps) {
  // Off in Client Setup, and not what this team already has.
  const modeOff = (k: string) => !offered(offer?.modes, k) && saved?.mode !== k
  const payOff = (k: string) => !offered(offer?.pay_bases, k) && saved?.pay_basis !== k
  // Picking a task picks its usual pay: Harvest is piece rate, the rest hourly, where Client Setup allows it.
  const taskPay = (task: string): Partial<Draft> => {
    const k: Draft['payBasis'] = task === 'Harvest' ? 'piece_rate' : 'hourly'
    return offered(offer?.pay_bases, k) ? { payBasis: k } : {}
  }
  const isNew = !d.id
  const induction = d.task === 'Induction'
  const rotating = isRotating(d.codeLife)
  const life = lifeOf(d.codeLife)
  const siteName = sites.find((s) => s.id === d.orchardId)?.name || saved?.site_name || ''
  // The stored value is always kept in the list, so a save never silently
  // changes it; an induction team is never offered as a default team.
  const groupOptions = groups.filter((g) => !g.is_archived || g.id === saved?.group_id)
  if (saved?.group_id && !groupOptions.some((g) => g.id === saved.group_id)) {
    groupOptions.push({ id: saved.group_id, name: saved.group_name || 'Current group', is_archived: true })
  }
  const defaultTeamOptions = teams.filter((t) => !t.is_induction && t.id !== d.id && (t.active || t.id === saved?.default_team_id))

  // The write in flight: 'save' or 'code', so only the pressed button says so.
  const [busyOn, setBusyOn] = useState<string | null>(null)
  const panelBusy = busy || busyOn !== null
  const slow = useSlow(panelBusy)

  const wrappedSave = async (confirmClear = false) => {
    setBusyOn('save')
    try {
      await onSave(confirmClear)
    } finally {
      setBusyOn(null)
    }
  }

  const wrappedReplaceCode = async () => {
    setBusyOn('code')
    try {
      await onReplaceCode()
    } finally {
      setBusyOn(null)
    }
  }

  // A layer (h 425-458): Escape, the scrim, the × and Cancel ask first once
  // the draft has moved. The list, sites and sets are loaded before a panel
  // can open, so the draft it opens with is the start.
  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged(d)
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: dirty && !panelBusy, el: panelEl, modal: true, focusFirst: true, busy: panelBusy })

  // Preassign: one team per site. The holder is read from the list, so a
  // taken role is refused before a save is even tried; the server refuses
  // it again (409) whatever the list said.
  const holder = d.orchardId ? teams.find((t) => t.preassign && t.orchard_id === d.orchardId && t.id !== d.id) : undefined
  const preShow = !induction && (d.mode === 'Harvest' || d.mode === 'Harvest readonly')
  const togglePre = () => {
    if (isNew || (!d.orchardId && !d.preassign)) return
    // D5.9: an inactive team cannot be the preassign team.
    if (!d.active && !d.preassign) return
    if (d.preassign) {
      patch({ preassign: false })
      setPreWarn(null)
    } else if (holder) setPreWarn(holder.name)
    else {
      patch({ preassign: true })
      setPreWarn(null)
    }
  }

  // A picker keeps the record's own value even when it is no longer a
  // current option, so an unrelated save cannot write the placeholder over it.
  const siteOptions = sites.filter((s) => !s.archived_at || s.id === d.orchardId)
  const products = d.productId && !PRODUCTS.includes(d.productId) ? [...PRODUCTS, d.productId] : PRODUCTS
  const lifeOptions = LIFETIMES.filter((l) => (!induction || !isRotating(l.key)) && (!l.legacy || l.key === d.codeLife))

  const blocked = !d.name.trim() ? 'Name needed' : !d.task ? 'Task needed' : !d.orchardId ? 'Site needed' : !d.clockOff ? 'Auto clock-off time needed' : null

  // New code / Reset key act on the saved team at once, so they follow the
  // saved lifetime; while the draft moves between rotating and fixed the
  // button waits for the save.
  const savedRotating = saved ? saved.code_rotating : rotating
  const codeBtnLabel = rotating ? 'Reset key' : 'New code'
  const codeBtnHint = isNew
    ? 'The first code is generated when you save'
    : savedRotating !== rotating
      ? 'Save the new lifetime first'
      : rotating
        ? 'Replaces this team’s signing key. Every supervisor phone picks up the new key on its next sync, and any code shown before then stops working.'
        : 'Generates a new code. The old one stops working straight away.'
  const codeText = rotating ? 'Rotating' : !isNew && saved?.access_code ? saved.access_code : '——————'
  const codeNote = rotating
    ? 'The typed code rotates with the QR and only shows on the supervisor’s phone, next to it. There is no fixed code to hand out.'
    : isNew || !saved?.access_code
      ? 'Generated when you save. Workers type it on the phone when there is no QR to scan.'
      : induction
        ? 'The team’s own code, for an induction run in person. Emailed invitations carry a personal code instead, so a new team code does not affect invitations already sent.'
        : 'Typed on the phone when a worker has no QR to scan. A new code stops the old one working straight away.'

  let lifeNote: string
  if (induction) {
    lifeNote = `Each invitation email carries its own code, valid for ${life.label} from when it is sent. After that it stops working; send the invitation again to issue a fresh one.`
  } else if (rotating) {
    lifeNote = `Rotating. Nothing to print or email. A phone on a team in Supervisor app mode at this site shows a fresh QR and typed code for this team, replaced every ${life.label}. A photo of the phone stops working when the code moves on.`
  } else if (isNew || !saved?.access_code || !saved.code_issued_at || saved.code_rotating) {
    lifeNote = `A fixed code, replaced automatically every ${life.label}. Printed QRs stop working when it is replaced.`
  } else {
    const next = saved.code_life === d.codeLife && saved.code_expires_at ? { date: new Date(saved.code_expires_at), passed: false } : nextReplacement(saved.code_issued_at, d.codeLife)
    lifeNote = next.passed
      ? `Under this lifetime the current code is already past its replacement date, so saving replaces it. Printed QRs stop working then, so reprint after saving.`
      : `Fixed code, replaced automatically on ${fmtDay(next.date)}. Printed QRs and anything emailed stop working then, so reprint after that date.`
  }

  let activeNote: string
  if (isNew) activeNote = 'A new team starts active and appears on the phones straight away.'
  else if (induction)
    activeNote = d.active
      ? 'Workers can complete their induction from here. No hours are recorded.'
      : 'Switched off. Its induction QR and code no longer join anyone to Personnel, so collect any printed codes in.'
  else if (!d.active && d.preassign && saved?.preassign)
    activeNote = 'Switching this team off also clears its preassign role, and the site will have no preassign team until another team is given it. You will be asked to confirm when you save.'
  else
    activeNote = d.active
      ? 'Workers can clock on to this team.'
      : 'Switched off. Its history stays and nobody can clock on from here. A worker offline with a printed QR can still scan onto it. That clock-on arrives when their phone next reaches a signal.'

  const flowRows: { key: 'stand' | 'reask' | 'binIdle'; label: string; note: string; desc: string }[] = []
  if (d.mode === 'Harvest')
    flowRows.push({ key: 'stand', label: 'Stand-down (seconds)', note: '0 = scan again immediately', desc: 'How long the phone waits before the same picker can scan another bucket.' })
  if (d.mode !== 'Supervisor')
    flowRows.push({ key: 'reask', label: 'Location re-ask (min)', note: '0 = never re-ask', desc: 'How long before the phone asks which row or block they are in again.' })
  if (d.mode === 'Receipt into a bin')
    flowRows.push({ key: 'binIdle', label: 'Bin idle (min)', note: '0 = a bin always resumes', desc: 'How long a part-filled bin sits idle before it stops accepting more.' })

  return (
    <div className={o.drawerScrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <div ref={panelEl} tabIndex={-1} className={`${o.drawer} ${styles.panel}`} role="dialog" aria-label={d.name.trim() || 'New team'}>
        <div className={o.drawerHead}>
          <span className={styles.bigSwatch} style={{ background: d.colour || '#e6ded0' }} />
          <div className={o.headText}>
            <h2 className={`${o.drawerTitle} ${styles.panelTitle}`}>{d.name.trim() || 'New team'}</h2>
            <p className={o.drawerSub}>
              {isNew ? 'A team is a task, a site and the phone settings that go with them' : `Team · ${siteName || 'No site'}`}
            </p>
          </div>
          <button type="button" className={o.close} aria-label="Close" onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={`${o.drawerBody} ${styles.panelBody} ${inertClass(panelBusy, slow)}`}>
          <div className={o.toggleRow}>
            <div className={o.toggleText}>
              <div className={o.toggleTitle}>Active</div>
              <div className={o.toggleSub}>{activeNote}</div>
            </div>
            <Switch on={d.active} label="Active" onClick={() => patch({ active: !d.active })} />
          </div>

          {clearAsk && !d.active && saved && (
            <div className={o.warn} role="alert" data-testid="clear-preassign">
              <strong>{saved.name}</strong> is the preassign team for {siteName || 'its site'}. Switching it off clears that role, so {siteName || 'the site'} will have no preassign team until you give the role to another team. The role is not passed on automatically. Both changes are saved together.
              <div style={{ marginTop: '0.5rem' }}>
                <button type="button" className={o.primary} disabled={panelBusy} onClick={() => wrappedSave(true)}>
                  Switch off and clear the role
                </button>
              </div>
            </div>
          )}

          {preShow && (
            <div className={d.preassign ? `${o.toggleRow} ${styles.pre} ${styles.preOn}` : `${o.toggleRow} ${styles.pre}`}>
              <div className={styles.preRow}>
                <div className={o.toggleText}>
                  <div className={styles.preTitle}>
                    <span className={o.toggleTitle}>Preassign team</span>
                    {d.preassign && <span className={styles.chipThis}>This team</span>}
                  </div>
                  <div className={o.toggleSub}>
                    {!d.active && !d.preassign
                      ? 'An inactive team cannot be the preassign team, because nobody can clock on to it. Switch the team on first.'
                      : d.preassign
                      ? 'The preassign station treats a worker’s first tag scan as their clock-on and puts them on this team. One team per site can do this.'
                      : holder
                        ? `A worker’s first tag scan at the preassign station clocks them onto the preassign team. ${holder.name} holds that role at ${siteName}.`
                        : `Switch on to make a worker’s first tag scan at the preassign station clock them onto this team. No team holds this at ${siteName || 'this site'} yet.`}
                  </div>
                </div>
                <Switch on={d.preassign} label="Preassign team" disabled={isNew || (!d.orchardId && !d.preassign) || (!d.active && !d.preassign)} onClick={togglePre} />
              </div>
              {preWarn && (
                <div className={o.warn} role="alert">
                  Can't switch on: <strong>{preWarn}</strong> is already the preassign team for this site, and there can only be one per site. Turn it off there first, or the station wouldn't know which team to clock people onto.
                </div>
              )}
            </div>
          )}

          {QC_MODES.includes(d.mode) && (
            <div className={`${o.toggleRow} ${styles.qc}`}>
              <label className={o.toggleTitle} htmlFor="tp-qc">
                Quality set
              </label>
              <select id="tp-qc" className={o.input} value={d.qualitySetId || ''} onChange={(e) => patch({ qualitySetId: e.target.value || null })}>
                {sets.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
                <option value="">No quality control</option>
              </select>
              <div className={o.toggleSub}>
                {d.qualitySetId
                  ? 'The list a QC person sees when they scan an item on this team. One set per team. Sets are built in Settings › Quality control.'
                  : 'This team cannot record quality. The app offers no quality scan at all. Choose a set to allow it.'}
              </div>
            </div>
          )}

          <div className={o.field}>
            <label className={o.fieldLabel} htmlFor="tp-name">
              Team name <span className={o.req}>required</span>
            </label>
            <input id="tp-name" className={d.name.trim() ? o.input : `${o.input} ${styles.inputNeeded}`} value={d.name} onChange={(e) => patch({ name: e.target.value })} placeholder="e.g. Alpha Pickers" />
          </div>

          <div className={o.grid2}>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="tp-task">
                Task
              </label>
              <select id="tp-task" className={d.task ? o.input : `${o.input} ${styles.unset}`} value={d.task} onChange={(e) => patch({ task: e.target.value, ...taskPay(e.target.value), ...(e.target.value === 'Induction' && isRotating(d.codeLife) ? { codeLife: '1w' } : {}) })}>
                <option value="">Pick a task</option>
                {TASKS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="tp-site">
                Site
              </label>
              <select id="tp-site" className={d.orchardId ? o.input : `${o.input} ${styles.unset}`} value={d.orchardId} onChange={(e) => patch({ orchardId: e.target.value })}>
                <option value="">Pick a site</option>
                {siteOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="tp-product">
                Primary product
              </label>
              <select id="tp-product" className={o.input} value={d.productId} onChange={(e) => patch({ productId: e.target.value })}>
                <option value="">None</option>
                {products.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            {!induction && (
              <div className={o.field}>
                <label className={o.fieldLabel} htmlFor="tp-pay">
                  Paid by
                </label>
                <select id="tp-pay" className={o.input} value={d.payBasis} onChange={(e) => patch({ payBasis: e.target.value as Draft['payBasis'] })}>
                  <option value="hourly" disabled={payOff('hourly')}>Hourly{payOff('hourly') ? ' (not turned on)' : ''}</option>
                  <option value="piece_rate" disabled={payOff('piece_rate')}>Piece rate{payOff('piece_rate') ? ' (not turned on)' : ''}</option>
                </select>
                {!offered(offer?.pay_bases, d.payBasis) && <div className={o.fieldHint}>Turned off in Client Setup. This team keeps it until changed.</div>}
              </div>
            )}
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="tp-group">
                Group
              </label>
              <select id="tp-group" className={o.input} value={d.groupId} onChange={(e) => patch({ groupId: e.target.value })}>
                <option value="">Default group</option>
                {groupOptions.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
              <div className={o.fieldHint}>Someone new who signs up in the Crew app on this team starts in this group.</div>
            </div>
            {induction && (
              <div className={o.field}>
                <label className={o.fieldLabel} htmlFor="tp-default-team">
                  Default team after induction
                </label>
                <select id="tp-default-team" className={o.input} value={d.defaultTeamId} onChange={(e) => patch({ defaultTeamId: e.target.value })}>
                  <option value="">None</option>
                  {defaultTeamOptions.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
                <div className={o.fieldHint}>Someone new who signs up on this team gets it as their default team. An induction team cannot be one.</div>
              </div>
            )}
          </div>

          <div className={o.field}>
            <div className={styles.colourHead}>
              <span className={o.fieldLabel}>Colour</span>
              <span className={styles.colourName}>{colourName(d.colour)}</span>
            </div>
            <div className={styles.swatches} role="radiogroup" aria-label="Colour">
              {TEAM_COLOURS.map((c) => (
                <button
                  key={c.hex}
                  type="button"
                  role="radio"
                  aria-checked={c.hex === d.colour.toLowerCase()}
                  aria-label={c.name}
                  title={c.name}
                  className={c.hex === d.colour.toLowerCase() ? styles.swatchOn : styles.swatchPick}
                  style={{ background: c.hex }}
                  onClick={() => patch({ colour: c.hex })}
                />
              ))}
            </div>
          </div>

          <div className={o.field}>
            <span className={o.fieldLabel}>
              Access code <span className={styles.muted2}>generated</span>
            </span>
            <div className={styles.codeRow}>
              <div className={styles.codeBox} data-testid="access-code">
                {codeText}
              </div>
              {canEdit && <PendingButton type="button" className={styles.codeBtn} busy={busyOn === 'code'} disabled={isNew || panelBusy || (busyOn !== null && busyOn !== 'code') || savedRotating !== rotating} title={codeBtnHint} pending="Updating…" onClick={wrappedReplaceCode}>
                {codeBtnLabel}
              </PendingButton>}
            </div>
            <span className={o.fieldHint}>{codeNote}</span>
          </div>

          <div className={o.field}>
            <label className={o.fieldLabel} htmlFor="tp-life">
              Code lifetime
            </label>
            <select id="tp-life" className={`${o.input} ${styles.lifeSelect}`} value={d.codeLife} onChange={(e) => patch({ codeLife: e.target.value as LifeKey })}>
              {lifeOptions.map((l) => (
                <option key={l.key} value={l.key}>
                  {l.option ?? l.label}
                </option>
              ))}
            </select>
            <span className={o.fieldHint}>{lifeNote}</span>
          </div>

          {induction && (
            <div className={styles.section}>
              <div>
                <div className={o.label}>Induction</div>
                <div className={o.fieldHint}>Send this before anyone's first day. Scanning the induction QR, or typing the code above, joins the worker to Personnel and nothing else.</div>
              </div>
              <div className={styles.good}>
                <strong>No timesheet is created.</strong> An induction scan is not a clock-on. No hours start, no day is opened, nothing reaches payroll. It only creates the worker's profile and links their phone, so they arrive on day one already set up.
              </div>
            </div>
          )}

          {!induction && (
            <div className={styles.section}>
              <div>
                <div className={o.label}>Dataphyll Crew App mode</div>
                <div className={o.fieldHint}>
                  {rotating
                    ? 'One mode at a time. It rides in the clock-on QR; the supervisor’s phone picks up a change on the next rotation.'
                    : 'One mode at a time. It rides in the clock-on QR, so reprint the QR after changing it.'}
                </div>
              </div>
              <div className={styles.modes} role="radiogroup" aria-label="Dataphyll Crew App mode">
                {MODES.map((m) => {
                  const on = m.key === d.mode
                  const off = modeOff(m.key)
                  return (
                    <button key={m.key} type="button" role="radio" aria-checked={on} disabled={off} title={off ? NOT_ON : undefined} className={on ? `${o.choice} ${o.choiceOn} ${styles.mode}` : off ? `${o.choice} ${styles.mode} ${styles.modeOff}` : `${o.choice} ${styles.mode}`} onClick={() => patch({ mode: m.key })}>
                      <span className={o.radio} />
                      <span className={styles.modeText}>
                        <span className={styles.modeTitle}>{modeLabel(m.key)}</span>
                        <span className={styles.modeDesc}>{off ? NOT_ON : on && !offered(offer?.modes, m.key) ? 'Turned off in Client Setup. This team keeps it until changed.' : m.desc}</span>
                      </span>
                    </button>
                  )
                })}
              </div>
              <div className={styles.flows}>
                {flowRows.map((r) => (
                  <div key={r.key} className={styles.flow}>
                    <div className={styles.flowText}>
                      <div className={styles.flowLabel}>{r.label}</div>
                      <div className={o.fieldHint}>
                        {r.desc} {r.note}
                      </div>
                    </div>
                    <input
                      className={d[r.key] === '' ? styles.flowInputBlank : styles.flowInput}
                      value={d[r.key]}
                      placeholder={`Default (${FLOW_DEFAULTS[r.key]})`}
                      inputMode="numeric"
                      aria-label={r.label}
                      onChange={(e) => patch({ [r.key]: digits(e.target.value) } as Partial<Draft>)}
                    />
                  </div>
                ))}
                {d.mode !== 'Supervisor' && (
                  <div className={styles.flow}>
                    <div className={styles.flowText}>
                      <div className={styles.flowLabel}>Location required</div>
                      <div className={o.fieldHint}>
                        On, a worker must set the row or block they are in before the phone will scan a bucket, show their tag or receipt an item. Off, setting it is up to them.
                      </div>
                    </div>
                    <Switch on={d.locationRequired} label="Location required" onClick={() => patch({ locationRequired: !d.locationRequired })} />
                  </div>
                )}
                {d.mode !== 'Supervisor' && (
                  <div className={styles.flow}>
                    <div className={styles.flowText}>
                      <div className={styles.flowLabel}>How pickers set their location</div>
                      <div className={o.fieldHint}>
                        Location QR only makes them scan the sign on the row or block, so they can't pick the wrong one from the list. Phones pick up a change on their next sync, with no card reprint.
                      </div>
                    </div>
                    <select
                      className={`${o.input} ${styles.flowSelect}`}
                      value={d.locationEntry}
                      aria-label="How pickers set their location"
                      onChange={(e) => patch({ locationEntry: e.target.value as Draft['locationEntry'] })}
                    >
                      {LOCATION_ENTRY_OPTIONS.map((x) => (
                        <option key={x.value} value={x.value}>
                          {x.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className={styles.flow}>
                  <div className={styles.flowText}>
                    <div className={styles.flowLabel}>Auto clock-off</div>
                    <div className={o.fieldHint}>
                      The latest a shift runs. A worker still clocked on is clocked off the next time the clock reads this, so 06:00 suits a night shift. It rides in the clock-on QR.
                    </div>
                  </div>
                  <input
                    type="time"
                    className={`${styles.flowInput} ${styles.flowInputTime}`}
                    value={d.clockOff}
                    aria-label="Auto clock-off"
                    onChange={(e) => patch({ clockOff: e.target.value.slice(0, 5) })}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={`${o.drawerFoot} ${styles.panelFoot}`}>
          <button type="button" className={o.ghost} disabled={panelBusy} onClick={requestClose}>
            {canEdit ? 'Cancel' : 'Done'}
          </button>
          {!canEdit && <span className={o.footNote}>Your permission set can view teams, not change them.</span>}
          {canEdit && blocked && <span className={o.footNote}>{blocked}</span>}
          {canEdit && (
            <PendingButton type="button" className={o.primary} busy={busyOn === 'save'} disabled={!!blocked || panelBusy || (busyOn !== null && busyOn !== 'save')} pending={isNew ? 'Creating…' : 'Saving…'} onClick={() => wrappedSave()}>
              {isNew ? 'Create team' : 'Save changes'}
            </PendingButton>
          )}
        </div>
      </div>
      {confirm}
    </div>
  )
}

function PageCheckbox({ checked, partial, onChange, disabled }: { checked: boolean; partial: boolean; onChange: () => void; disabled: boolean }) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = partial
  }, [partial])
  return <input ref={ref} type="checkbox" aria-label="Select every team shown" checked={checked} disabled={disabled} onChange={onChange} />
}
